import { Filter, GlProgram, UniformGroup } from 'pixi.js';

// #version 300 es is required for the AND/OR/XOR modes' integer bitwise ops
// below; it also makes Pixi's GlProgram treat both stages as GLSL ES 3.00.
const vertex = `#version 300 es
in vec2 aPosition;
out vec2 vTextureCoord;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

vec4 filterVertexPosition(void)
{
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    return vec4(position, 0.0, 1.0);
}
vec2 filterTextureCoord(void)
{
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
}
void main(void)
{
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
}
`;

// Ported from AreaTint.tres's shader (TRANS/ADD/SUB/AND/OR/XOR vs the screen behind it).
const fragment = `#version 300 es
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;

uniform vec3 uTintColor;
uniform float uTintAlpha;
uniform int uTintMode; // 0=trans 1=add 2=sub 3=and 4=or 5=xor

void main(void)
{
    vec4 c = texture(uTexture, vTextureCoord);
    vec3 outRgb = c.rgb;

    if (uTintMode == 0) {
        outRgb = mix(c.rgb, uTintColor, uTintAlpha);
    } else if (uTintMode == 1) {
        outRgb = c.rgb + uTintColor;
    } else if (uTintMode == 2) {
        outRgb = c.rgb - uTintColor;
    } else {
        ivec3 ci = ivec3(c.rgb * 255.0);
        ivec3 ti = ivec3(uTintColor * 255.0);
        ivec3 bits = uTintMode == 3 ? (ci & ti) : uTintMode == 4 ? (ci | ti) : (ci ^ ti);
        outRgb = vec3(bits) / 255.0;
    }
    finalColor = vec4(outRgb, c.a);
}
`;

export type TintMode = 'trans' | 'add' | 'sub' | 'and' | 'or' | 'xor';
const MODE_INDEX: Record<TintMode, number> = { trans: 0, add: 1, sub: 2, and: 3, or: 4, xor: 5 };

/**
 * Area extra-data `Tint`/`TintInk`/`TintTrans` overlay (GDKnyttGame.cs
 * applyTint / AreaTint.tres). Applied as a Pixi filter on the world layer so
 * it reads back the already-rendered scene, matching Godot's SCREEN_TEXTURE.
 */
export class TintFilter extends Filter {
  constructor() {
    const uniforms = new UniformGroup({
      uTintColor: { value: [0, 0, 0], type: 'vec3<f32>' },
      uTintAlpha: { value: 0, type: 'f32' },
      uTintMode: { value: 0, type: 'i32' },
    });
    super({
      glProgram: new GlProgram({ vertex, fragment, name: 'area-tint-filter' }),
      resources: { tintUniforms: uniforms },
    });
  }

  set(r: number, g: number, b: number, alpha: number, mode: TintMode): void {
    const u = this.resources.tintUniforms.uniforms;
    u.uTintColor = [r / 255, g / 255, b / 255];
    u.uTintAlpha = alpha;
    u.uTintMode = MODE_INDEX[mode];
  }
}
